import { Mail, Instagram, Twitter, Facebook, Github } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useDesignSystemV2 } from '../../context/DesignSystemV2Context';
import { useExperience } from '../../context/ExperienceContext';

const CHROMELESS_ROUTES = ['/beta-signin', '/beta-signup', '/unsubscribe'];

export function Footer() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const { activeV2Theme, isV2Active } = useDesignSystemV2();
  const { isMangaV3 } = useExperience();
  const isV2Standard = isV2Active && activeV2Theme === 'new-standard';
  const isV2Manga = (isV2Active && activeV2Theme === 'new-manga') || isMangaV3;
  const isV2Ukiyo = isV2Active && activeV2Theme === 'new-ukiyo';
  const currentYear = new Date().getFullYear();

  const normalizedPath = location.pathname.replace(/\/$/, '').toLowerCase() || '/';
  if (CHROMELESS_ROUTES.includes(normalizedPath)) return null;

  const productLinks = [
    { label: t('nav.dream'), to: '/dream' },
    { label: t('nav.travel'), to: '/travel' },
    { label: t('nav.share'), to: '/share' },
    { label: t('nav.achievements'), to: '/achievements' },
    { label: t('footer.essentials'), to: '/essentials' },
    { label: t('footer.taxFree'), to: '/tax-free' },
  ];

  const footerLinks = {
    company: [
      { label: t('footer.about'), to: '/about' },
      { label: t('footer.help'), to: '/help' },
      { label: t('footer.faq'), to: '/faq' },
      { label: t('footer.taxFree'), to: '/tax-free' },
      { label: t('footer.contact'), to: '/contact' },
      { label: t('footer.blog'), to: '/blog' },
    ],
    legal: [
      { label: t('footer.privacy'), to: '/privacy' },
      { label: t('footer.terms'), to: '/terms' },
      { label: t('footer.cookies'), to: '/cookies' },
      { label: t('footer.cookiePreferences'), to: '/cookies#manage' },
    ],
  };

  const socialLinks: { icon: typeof Twitter; href: string; label: string }[] = [];

  // V2 theme palette resolved per mode. Each theme is a self-contained set
  // of colors + fonts so the JSX can stay 1:1 with the existing structure.
  const v2 = isV2Manga
    ? {
        bgClass: '',
        bgStyle: { background: '#0A0A0A', borderTop: '4px solid #E8192C' } as const,
        body: '#fff',
        muted: '#c8c0b8',
        dim: '#8a8580',
        link: '#fff',
        linkHover: '#E8192C',
        heading: '#fff',
        headingFont: "'Bangers', 'Impact', cursive",
        headingTransform: 'uppercase' as const,
        headingTracking: '1.5px',
        bodyFont: "'Plus Jakarta Sans', sans-serif",
        logoBg: '#E8192C',
        logoRadius: 0,
        logoBorder: '2px solid #fff',
        wordmarkFont: "'Bangers', 'Impact', cursive",
        wordmarkExtra: { letterSpacing: '1.5px', textTransform: 'uppercase' as const },
        separator: 'rgba(232,25,44,0.4)',
      }
    : isV2Ukiyo
    ? {
        bgClass: '',
        bgStyle: { background: '#0D2E52', borderTop: '1px solid #B8860B' } as const,
        body: '#E8DFC8',
        muted: '#C8B894',
        dim: 'rgba(232,223,200,0.55)',
        link: '#E8DFC8',
        linkHover: '#D4A017',
        heading: '#E8DFC8',
        headingFont: "'Shippori Mincho', 'Noto Serif JP', serif",
        headingTransform: 'none' as const,
        headingTracking: '0',
        bodyFont: "'Plus Jakarta Sans', sans-serif",
        logoBg: '#C0392B',
        logoRadius: 2,
        logoBorder: '1px solid #B8860B',
        wordmarkFont: "'Shippori Mincho', 'Noto Serif JP', serif",
        wordmarkExtra: { fontWeight: 700, letterSpacing: '-0.01em' },
        separator: 'rgba(184,134,11,0.4)',
      }
    : isV2Standard
    ? {
        bgClass: '',
        bgStyle: { background: 'rgb(42 40 37)', borderTop: '1px solid rgba(245,173,196,0.25)' } as const,
        body: 'rgb(232 226 214)',
        muted: 'rgb(181 174 158)',
        dim: 'rgb(148 140 124)',
        link: 'rgb(232 226 214)',
        linkHover: 'rgb(184 92 56)',
        heading: '#fff',
        headingFont: "'Fraunces', Georgia, serif",
        headingTransform: 'uppercase' as const,
        headingTracking: '2.2px',
        bodyFont: "'Plus Jakarta Sans', sans-serif",
        logoBg: 'rgb(160 120 80)',
        logoRadius: 8,
        logoBorder: 'none',
        wordmarkFont: "'Fraunces', Georgia, serif",
        wordmarkExtra: { fontVariationSettings: "'opsz' 144, 'SOFT' 40", fontWeight: 500 },
        separator: 'rgb(86 80 73)',
      }
    : null;

  if (v2) {
    return (
      <footer style={{ ...v2.bgStyle, color: v2.body, fontFamily: v2.bodyFont }}>
        <div className="container mx-auto px-4 py-16">
          <div className={`grid grid-cols-1 md:grid-cols-2 ${user ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-12`}>
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center space-x-2 mb-4">
                <span
                  className="flex items-center justify-center"
                  style={{ width: 28, height: 28, fontSize: 14, fontWeight: 700, fontFamily: "'Noto Serif JP', serif", lineHeight: 1, color: '#fff', background: v2.logoBg, borderRadius: v2.logoRadius, border: v2.logoBorder }}
                >
                  旅
                </span>
                <span className="text-xl font-bold" style={{ color: v2.heading, fontFamily: v2.wordmarkFont, ...v2.wordmarkExtra }}>
                  TripGenesys Japan
                </span>
              </Link>
              <p className="leading-relaxed mb-6 max-w-sm" style={{ color: v2.muted }}>
                {t('footer.tagline')}
              </p>
            </div>

            {user && (
              <div>
                <h4 className="mb-4 text-sm" style={{ color: v2.heading, fontFamily: v2.headingFont, fontWeight: 700, textTransform: v2.headingTransform, letterSpacing: v2.headingTracking }}>{t('footer.product')}</h4>
                <ul className="space-y-3">
                  {productLinks.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="transition-colors text-sm" style={{ color: v2.muted }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = v2.linkHover; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = v2.muted; }}>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <h4 className="mb-4 text-sm" style={{ color: v2.heading, fontFamily: v2.headingFont, fontWeight: 700, textTransform: v2.headingTransform, letterSpacing: v2.headingTracking }}>{t('footer.company')}</h4>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="transition-colors text-sm" style={{ color: v2.muted }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = v2.linkHover; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = v2.muted; }}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="mb-4 text-sm" style={{ color: v2.heading, fontFamily: v2.headingFont, fontWeight: 700, textTransform: v2.headingTransform, letterSpacing: v2.headingTracking }}>{t('footer.legal')}</h4>
              <ul className="space-y-3">
                {footerLinks.legal.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="transition-colors text-sm" style={{ color: v2.muted }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = v2.linkHover; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = v2.muted; }}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                <a
                  href="mailto:hello@tripgenesys.com"
                  className="flex items-center space-x-2 transition-colors text-sm"
                  style={{ color: v2.muted }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = v2.linkHover; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = v2.muted; }}
                >
                  <Mail size={16} />
                  <span>hello@tripgenesys.com</span>
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8" style={{ borderTop: `1px solid ${v2.separator}` }}>
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-sm" style={{ color: v2.muted }}>
                {t('footer.copyright', { year: currentYear })}
              </p>
              <p className="text-xs" style={{ color: v2.dim }}>
                {t('footer.crafted')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-gray-900 dark:bg-gray-950 text-gray-300">
      <div className="container mx-auto px-4 py-16">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${user ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-12`}>
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-4">
              <span
                className="flex items-center justify-center rounded-md bg-secondary-500"
                style={{ width: 28, height: 28, fontSize: 14, fontWeight: 700, fontFamily: "'Noto Serif JP', serif", lineHeight: 1, color: '#fff' }}
              >
                旅
              </span>
              <span className="text-xl font-display font-bold text-white">TripGenesys Japan</span>
            </Link>
            <p className="text-gray-400 leading-relaxed mb-6 max-w-sm">
              {t('footer.tagline')}
            </p>
            {socialLinks.length > 0 && <div className="flex space-x-3">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 rounded-xl bg-gray-800 hover:bg-secondary-600 flex items-center justify-center transition-all duration-200 hover:-translate-y-0.5"
                  aria-label={social.label}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>}
          </div>

          {user && (
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">{t('footer.product')}</h4>
              <ul className="space-y-3">
                {productLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="text-gray-400 hover:text-secondary-400 transition-colors text-sm">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">{t('footer.company')}</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-secondary-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold mb-4 text-sm tracking-wider uppercase">{t('footer.legal')}</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-gray-400 hover:text-secondary-400 transition-colors text-sm">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <a
                href="mailto:hello@tripgenesys.com"
                className="flex items-center space-x-2 text-gray-400 hover:text-secondary-400 transition-colors text-sm"
              >
                <Mail size={16} />
                <span>hello@tripgenesys.com</span>
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <p className="text-gray-600 text-xs">
              {t('footer.crafted')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
